import type { ReactNode } from 'react';

import {
  RiForward10Line,
  RiFullscreenExitLine,
  RiFullscreenLine,
  RiReplay10Line,
} from '@remixicon/react';

import type {
  CsrPlayerElement,
  MetricEvent,
} from '@spotify-internal/csr-player';
import { ButtonIcon } from '@spotify-internal/encore-web';
import {
  Tooltip as EncoreTooltip,
  TooltipRoot,
} from '@spotify-internal/encore-web/next';

import { FullPlaybackControls } from './FullPlaybackControls';
import { SimplifiedPlaybackControls } from './SimplifiedPlaybackControls';
import type { FullscreenControl } from './types';
import { type PlaybackDefaults, usePlaybackState } from './usePlaybackState';

type PlaybackControlsVariant = 'full' | 'simplified';

type PlaybackControlsProps = {
  player: CsrPlayerElement;
  metricEvents?: MetricEvent[];
  variant?: PlaybackControlsVariant;
  initialPageUrl?: string;
  playbackDefaults?: PlaybackDefaults;
  onEventMarkerClick?: (timestampSeconds: number) => void;
  fullscreenControl?: FullscreenControl;
  menuContainer?: HTMLElement;
};

const FullscreenButton = ({
  fullscreenControl,
}: {
  fullscreenControl: FullscreenControl;
}): ReactNode => {
  const label = fullscreenControl.isFullscreen
    ? 'Exit fullscreen'
    : 'Enter fullscreen';

  return (
    <TooltipRoot>
      <ButtonIcon
        size="small"
        variant="tertiary"
        aria-label={label}
        data-track-id="button.fullscreen"
        data-track-action="update"
        data-track-data-enabled={!fullscreenControl.isFullscreen}
        onClick={fullscreenControl.onToggle}
      >
        {fullscreenControl.isFullscreen ? (
          <RiFullscreenExitLine size={18} aria-hidden />
        ) : (
          <RiFullscreenLine size={18} aria-hidden />
        )}
      </ButtonIcon>
      <EncoreTooltip>{label}</EncoreTooltip>
    </TooltipRoot>
  );
};

const SeekButton = ({
  seconds,
  onSeek,
}: {
  seconds: -10 | 10;
  onSeek: (seconds: number) => void;
}): ReactNode => {
  const isRewind = seconds < 0;
  const label = isRewind ? 'Rewind 10 seconds' : 'Forward 10 seconds';

  return (
    <TooltipRoot>
      <ButtonIcon
        size="small"
        variant="tertiary"
        aria-label={label}
        data-track-id="button.seek"
        data-track-action="seek"
        data-track-data-seconds={seconds}
        onClick={() => onSeek(seconds)}
      >
        {isRewind ? (
          <RiReplay10Line size={18} aria-hidden />
        ) : (
          <RiForward10Line size={18} aria-hidden />
        )}
      </ButtonIcon>
      <EncoreTooltip>{label}</EncoreTooltip>
    </TooltipRoot>
  );
};

export const PlaybackControls = ({
  player,
  metricEvents = [],
  variant = 'full',
  initialPageUrl,
  playbackDefaults,
  onEventMarkerClick,
  fullscreenControl,
  menuContainer,
}: PlaybackControlsProps) => {
  const playbackState = usePlaybackState(player, {
    defaults: playbackDefaults,
  });
  const fullscreenButton = fullscreenControl ? (
    <FullscreenButton fullscreenControl={fullscreenControl} />
  ) : null;
  const rewindButton = (
    <SeekButton seconds={-10} onSeek={playbackState.handleSeekBySeconds} />
  );
  const forwardButton = (
    <SeekButton seconds={10} onSeek={playbackState.handleSeekBySeconds} />
  );

  if (variant === 'simplified') {
    return (
      <SimplifiedPlaybackControls
        player={player}
        metricEvents={metricEvents}
        playbackState={playbackState}
        rewindButton={rewindButton}
        forwardButton={forwardButton}
        fullscreenButton={fullscreenButton}
        menuContainer={menuContainer}
      />
    );
  }

  return (
    <FullPlaybackControls
      player={player}
      metricEvents={metricEvents}
      initialPageUrl={initialPageUrl}
      playbackState={playbackState}
      onEventMarkerClick={onEventMarkerClick}
      rewindButton={rewindButton}
      forwardButton={forwardButton}
      fullscreenButton={fullscreenButton}
      menuContainer={menuContainer}
    />
  );
};
